import { useEffect } from "react";
import { createParam } from "solito";

import { Flag } from "@medbillai/utils/flag";

import { isNonProd } from "../environment";
import { flagOverrideStorage } from "../storage/instances";

// QA / internal override for any LaunchDarkly flag. Set via `?ff-<flag>=<value>`
// on any pre-auth URL; persists to sync device storage so subsequent renders
// and cold opens keep it. Clear via the in-app dev menu (native) or DevTools
// (web). For a guaranteed reset after clearing, fully terminate and relaunch
// the app — the OS retains the original deep-link URL for the process
// lifetime, so it'd otherwise re-apply on the next JS reload.
//
// Enabled for local development builds and for the deployed dev environment;
// stripped from true production. See `environment.ts` for the detection logic.
const ENABLED = isNonProd();

const { useParams } = createParam<Record<string, string | undefined>>();

const parse = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
};

const useRuntimeFlagOverrideImpl = (flag: Flag): unknown => {
  const { params } = useParams();
  const urlValue = params[`ff-${flag}`];

  useEffect(() => {
    if (typeof urlValue === "string") {
      flagOverrideStorage.set(flag, urlValue);
    }
  }, [urlValue, flag]);

  if (typeof urlValue === "string") {
    return parse(urlValue);
  }
  const stored = flagOverrideStorage.get(flag);
  if (stored !== null) {
    return parse(stored);
  }
  return undefined;
};

export const useRuntimeFlagOverride: (flag: Flag) => unknown = ENABLED
  ? useRuntimeFlagOverrideImpl
  : () => undefined;

const readUrlOverride = (flag: Flag): string | null =>
  typeof window === "undefined" || typeof window.location?.search !== "string"
    ? null
    : new URLSearchParams(window.location.search).get(`ff-${flag}`);

/**
 * Non-hook override read for imperative call sites (e.g. evaluating a flag at
 * camera launch). A `?ff-` param on the current web URL wins and is persisted,
 * as the hook does; otherwise persisted storage.
 */
export const getRuntimeFlagOverride = (flag: Flag): unknown => {
  if (!ENABLED) {
    return undefined;
  }
  const urlValue = readUrlOverride(flag);
  if (urlValue !== null) {
    flagOverrideStorage.set(flag, urlValue);
    return parse(urlValue);
  }
  const stored = flagOverrideStorage.get(flag);
  return stored === null ? undefined : parse(stored);
};

export const listFlagOverrides = (): Array<{ flag: Flag; value: string }> => {
  if (!ENABLED) {
    return [];
  }
  return Object.values(Flag).flatMap(flag => {
    const value = flagOverrideStorage.get(flag);
    return value === null ? [] : [{ flag, value }];
  });
};

export const clearAllFlagOverrides = (): void => {
  if (!ENABLED) {
    return;
  }
  for (const flag of Object.values(Flag)) {
    flagOverrideStorage.remove(flag);
  }
};
